import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Svg, { Circle, Path, G, Text as SvgText } from 'react-native-svg';
import { useTheme } from '../../contexts/ThemeContext';
import { FONTS } from '../../constants/config';
import { getStatusColor, CHART_COLORS } from './chartStyles';

import { tokens } from '@design';
// ============================================================
// Types
// ============================================================

interface GaugeChartProps {
  value: number;
  maxValue?: number;
  size?: number;
  strokeWidth?: number;
  label?: string;
  showDot?: boolean;
  color?: string;
  targetValue?: number;
  showTarget?: boolean;
}

interface CircularGaugeProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
  showDot?: boolean;
  label?: string;
}

interface SegmentedGaugeProps {
  actual: number;
  target: number;
  size?: number;
  strokeWidth?: number;
  showDot?: boolean;
}

// ============================================================
// Circular Gauge Component (Full Circle)
// ============================================================

export const CircularGauge = ({
  percentage,
  size = 120,
  strokeWidth = 12,
  color,
  backgroundColor,
  showDot = true,
  label,
}: CircularGaugeProps) => {
  const { colors, isDark } = useTheme();

  // Ensure percentage is between 0-100
  const safePercentage = Math.min(Math.max(percentage, 0), 100);

  // Determine color based on percentage if not provided
  const gaugeColor = color || getStatusColor(safePercentage);
  const trackColor = backgroundColor || (isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["200"]);

  // Add padding for dot
  const dotPadding = showDot ? 10 : 0;
  const svgSize = size + dotPadding * 2;
  const center = svgSize / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (safePercentage / 100) * circumference;

  // Calculate dot position at END of progress arc
  const angle = ((safePercentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={[styles.gaugeContainer, { width: svgSize, height: svgSize }]}>
      <Svg width={svgSize} height={svgSize}>
        {/* Background Circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress Arc */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={gaugeColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot Indicator */}
        {showDot && safePercentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={gaugeColor}
            stroke={colors.card}
            strokeWidth={2}
          />
        )}
      </Svg>

      {/* Center Content */}
      <View style={styles.centerContent}>
        <Text style={[styles.percentageText, { color: colors.textPrimary, fontSize: size * 0.22 }]}>
          {Number(safePercentage).toFixed(2)} %
        </Text>
        {label && (
          <Text style={[styles.labelText, { color: colors.textMuted, fontSize: size * 0.1 }]}>
            {label}
          </Text>
        )}
      </View>
    </View>
  );
};

// ============================================================
// Segmented Gauge Component (Actual vs Target)
// ============================================================

export const SegmentedGauge = ({
  actual,
  target,
  size = 140,
  strokeWidth = 12,
  showDot = true,
}: SegmentedGaugeProps) => {
  const { colors, isDark } = useTheme();

  const trackColor = isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["200"];

  // Add padding for dot
  const dotPadding = showDot ? 10 : 0;
  const svgSize = size + dotPadding * 2;
  const center = svgSize / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;

  // Calculate percentages
  const actualPercentage = Math.min(actual, 100);
  const targetPercentage = Math.min(target, 100);

  const actualOffset = circumference - (actualPercentage / 100) * circumference;
  const targetOffset = circumference - (targetPercentage / 100) * circumference;

  // Calculate dot position for actual value
  const actualAngle = (actualPercentage / 100) * 360;
  const dotX = center + radius * Math.cos((actualAngle * Math.PI) / 180);
  const dotY = center + radius * Math.sin((actualAngle * Math.PI) / 180);

  return (
    <View style={[styles.gaugeContainer, { width: svgSize, height: svgSize }]}>
      <Svg width={svgSize} height={svgSize} style={{ transform: [{ rotate: '-90deg' }] }}>
        {/* Background Circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Target Circle (lighter green) */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={CHART_COLORS.gauge.target}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={targetOffset}
          strokeLinecap="round"
        />
        {/* Actual Circle (darker green) */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={CHART_COLORS.gauge.actual}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={actualOffset}
          strokeLinecap="round"
        />
        {/* Dot Indicator */}
        {showDot && actualPercentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={6.5}
            fill={CHART_COLORS.gauge.actual}
            stroke={colors.card}
            strokeWidth={2}
          />
        )}
      </Svg>

      {/* Center Content */}
      <View style={styles.centerContent}>
        <Text style={[styles.percentageText, { color: colors.textPrimary, fontSize: size * 0.21 }]}>
          % {Number(actual).toFixed(2)}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Semi-Circle Gauge Component
// ============================================================

interface SemiCircleGaugeProps {
  value: number;
  maxValue?: number;
  size?: number;
  strokeWidth?: number;
  label?: string;
  color?: string;
}

export const SemiCircleGauge = ({
  value,
  maxValue = 100,
  size = 160,
  strokeWidth = 14,
  label,
  color,
}: SemiCircleGaugeProps) => {
  const { colors, isDark } = useTheme();

  const percentage = Math.min((value / maxValue) * 100, 100);
  const gaugeColor = color || getStatusColor(percentage);
  const trackColor = isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["200"];

  // Semi-circle calculations
  const width = size;
  const height = size * 0.625; // Semi-circle height ratio
  const radius = (size - strokeWidth) / 2;
  const centerX = size / 2;
  const centerY = height - strokeWidth / 2;

  // Arc path for 180 degrees (semi-circle)
  const arcLength = Math.PI * radius;
  const strokeDashoffset = arcLength - (percentage / 100) * arcLength;

  // Arc path
  const arcPath = `M ${strokeWidth / 2} ${centerY} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${centerY}`;

  return (
    <View style={[styles.semiGaugeContainer, { width, height: height + 30 }]}>
      <Svg width={width} height={height}>
        {/* Background Arc */}
        <Path
          d={arcPath}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
        />
        {/* Progress Arc */}
        <Path
          d={arcPath}
          stroke={gaugeColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={arcLength}
          strokeDashoffset={strokeDashoffset}
        />
      </Svg>

      {/* Value Display */}
      <View style={[styles.semiGaugeCenterContent, { top: height - 40 }]}>
        <Text style={[styles.semiGaugeValue, { color: colors.textPrimary }]}>
          %{value.toFixed(2)}
        </Text>
      </View>

      {/* Label */}
      {label && (
        <Text style={[styles.semiGaugeLabel, { color: colors.textMuted }]}>
          {label}
        </Text>
      )}
    </View>
  );
};

// ============================================================
// Styles
// ============================================================

const getStyles = (isRTL: boolean) => StyleSheet.create({
  gaugeContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  centerContent: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  percentageText: {
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    textAlign: 'center',
  },
  labelText: {
    fontFamily: FONTS.regular,
    textAlign: 'center',
    marginTop: tokens.spacing.absolute["0_5"],
  },
  semiGaugeContainer: {
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  semiGaugeCenterContent: {
    position: 'absolute',
    alignItems: 'center',
    start: 0,
    end: 0,
  },
  semiGaugeValue: {
    fontSize: tokens.typography["heading 3"]["font size"],
    fontFamily: FONTS.bold,
    textAlign: 'center',
  },
  semiGaugeLabel: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    textAlign: 'center',
    marginTop: tokens.spacing.absolute["1"],
  },
});

export default CircularGauge;

const styles = getStyles(true);
